import { ParamMap } from '@angular/router';
import {
  HotelSearchedRoomRate, HotelSearchedRoom, HotelRoomsByCode
} from '@hotels/hotel-results/entities/hotel-searched';
import { HotelDetailPayload, HotelDetailDeepLinkPayload } from '@hotels/hotel-detail/entities/hotel-detail.payload';
import { HotelDetailState } from '@hotels/hotel-detail/store/reducers/hotel-detail.reducer';
import { MapMarkerInputs } from '@shared/map/constants/map.constants';
import { currencyFormat } from '@hotels/hotel-results/helpers/hotels.helpers';
import { mapPayloadHotelRooms } from '@ux/mappers/guest.mapper';
import { HotelByCodePackagePayload, HotelDetailPackagePayload } from '@packages/packages-result/entities/hotel-detail';
import { SalesType } from '@app/modules/core/constants/sales-type';
import { isGroupedViewByHotelDetail } from '@hotels/hotel-detail/mappers/is-grouped-view.mapper';
import { getBaseRequestSettings } from '@app/mappers/reguest-settings.mapper';
import { Price } from '@app/entities/price';

interface RoomGroupByType {
  [key: string]: HotelSearchedRoom[];
}

export function mapHotelDetailPayload(params: ParamMap): HotelDetailPayload {
  return {
    SessionToken: params.get('session_token'),
    SearchId: params.get('search_id'),
    HotelOptionId: params.get('hotel_option_id'),
    Rooms: mapPayloadHotelRooms(params.get('rooms'))
  };
}

export function mapHotelDetailDeeplinkPackagesPayload(params: ParamMap, isMobile: boolean): HotelByCodePackagePayload {
  return {
    HotelCode: `${params.get('hotel_name')}-${params.get('arrival')}`,
    DeparturePoint: params.get('departure'),
    ArrivalPoint: params.get('arrival'),
    DepartureDate: params.get('departure_date'),
    ArrivalDate: params.get('arrival_date'),
    Rooms: mapPayloadHotelRooms(params.get('rooms')),
    RequestSettings: getBaseRequestSettings(isMobile, SalesType.Package)
  };
}

export function mapHotelDetailPackagesPayload(params: ParamMap): HotelDetailPackagePayload {
  return {
    searchIdHotel: params.get('search_hotel_id'),
    hotelOptionId: params.get('hotel_option_id'),
    searchIdAir: params.get('search_air_id'),
    optionIdAir: params.get('air_option_id'),
    sessionToken: params.get('session_token'),
    Rooms: mapPayloadHotelRooms(params.get('rooms')),
  };
}

export function mapHotelDetailDeepLinkPayload(params: ParamMap, isMobile: boolean): HotelDetailDeepLinkPayload {
  return {
    HotelId: `${params.get('hotel_id')}`,
    CheckIn: params.get('departure_date'),
    CheckOut: params.get('arrival_date'),
    Rooms: mapPayloadHotelRooms(params.get('rooms')),
    CurrencyCode: params.get('currency'),
    CombineRooms: false,
    RequestSettings: getBaseRequestSettings(isMobile),
    MaxResults: 1
  };
}

function mapRooms(state: HotelDetailState): HotelSearchedRoomRate[] {
  return state.Result && state.Result.RoomRates ? state.Result.RoomRates.map(roomRate => ({
    ...roomRate,
    Rooms: roomRate.RoomIds.map(data => {
      return state.Result.Rooms.find(room => room.RoomId.toString() === data.toString());
    })
  })) : [];
}

export function mapHotelDetailRoom(state: HotelDetailState): HotelDetailState {
  if (Object.prototype.hasOwnProperty.call(state, 'Result')) {
    const rooms = mapRooms(state);
    const detail = {
      ...state,
      Result: {
        ...state.Result,
        RoomRates: rooms,
        LowerRoom: rooms.reduce((min, currentValue) =>
          currentValue?.Price?.Amount < min?.Price?.Amount ? currentValue : min, mapRooms(state)[0])
      }
    };
    return detail;
  } else return state;
}

export function mapHotelDetailMarkers(state: HotelDetailState): MapMarkerInputs[] {
  return state && state.Info ? [{
    position: { lat: state.Info.Latitude, lng: state.Info.Longitude },
    label: currencyFormat(state.Result.PriceFrom)
  }] : [];
}

export function mapRoomsGroup(state: HotelDetailState): HotelDetailState {
  if (isGroupedViewByHotelDetail(state?.Result?.Rooms, state?.Result?.RoomRates)) {
    return {
      ...state,
      Result: {
        ...state?.Result,
        RoomsByCode: getRoomsByCode(state?.Result?.Rooms, state?.Result?.RoomRates, state?.Result?.MicroStay),
      }
    };
  } else {
    return {
      ...state,
      Result: {
        ...state?.Result,
        RoomRates: roomRatesPerLowestPrice(state?.Result?.RoomRates),
        RoomsByCode: [],
      }
    };
  }
}

function getRoomsByCode(rooms: HotelSearchedRoom[], rates: HotelSearchedRoomRate[], isByhours: boolean): HotelRoomsByCode[] {
  let validRoomsByRate = [] as HotelRoomsByCode[];
  const groups = groupRoomOptionsByKeys(rooms, 'RoomCode');
  
  Object.keys(groups).forEach((roomCode) => {
    const roomsByGroup: HotelSearchedRoom[] = groups[roomCode];

    let validRooms = roomsByGroup
      .map(room => {
        const roomRateById = rates.find(rate => rate.RoomIds.includes(room.RoomId));
        return { ...room, RoomRateById: roomRateById };
      })
      .filter(room => !!room?.RoomRateById && !!Object.keys(room?.RoomRateById)?.length);
    
    let filterMinRepeat = validRooms;
    if (!isByhours) {
      filterMinRepeat = validRooms.filter((room, index, self) =>
        self.findIndex(v =>
          v.BoardDescription === room.BoardDescription &&
            v.RoomRateById.RefundableInfo === room.RoomRateById.RefundableInfo
        ) === index);
    }

    if (filterMinRepeat?.length) {
      validRoomsByRate.push({
        Code: roomCode,
        Description: filterMinRepeat?.[0]?.Description,
        Images: filterMinRepeat?.[0]?.Images || [],
        Beds: filterMinRepeat?.[0]?.Beds || [],
        Amenities: filterMinRepeat?.[0]?.Amenities || [],
        RoomsByRate: filterMinRepeat
      });
    }
  });
  validRoomsByRate = roomsByRateOnRoomsByCodePerLowestPrice(validRoomsByRate);
  validRoomsByRate = roomsByCodePerLowestPrice(validRoomsByRate);
  return validRoomsByRate;
}

function groupRoomOptionsByKeys(collection: HotelSearchedRoom[], keys: string): RoomGroupByType {
  if (collection?.length && keys?.length) {
    const groupBy = {};
    collection.forEach(item => {
      const valueOfProp = roomValueOfProp(keys, item);
      groupBy[valueOfProp] = [
        ...groupBy[valueOfProp] || [], item
      ];
    });
    return groupBy;
  } else return {};
}

function roomValueOfProp(keys: string, item: HotelSearchedRoom): string | number {
  return keys.split('.').reduce((obj: HotelSearchedRoom, prop: string) => obj && obj[prop], item);
}

function roomsByRateOnRoomsByCodePerLowestPrice(validRoomsByRate: HotelRoomsByCode[]): HotelRoomsByCode[] {
  return validRoomsByRate.map(room => {
    if (room?.RoomsByRate?.length > 1) {
      return { ...room, RoomsByRate: roomsByRatePerLowestPrice(room?.RoomsByRate) };
    } else {
      return room;
    }
  });
}

function roomsByCodePerLowestPrice(validRoomsByRate: HotelRoomsByCode[]): HotelRoomsByCode[] {
  if (validRoomsByRate?.length > 1) {
    return validRoomsByRate.sort((optA: HotelRoomsByCode, optB: HotelRoomsByCode) => {
      const optALowestPrice = optA?.RoomsByRate?.[0]?.RoomRateById?.Price;
      const optBLowestPrice = optB?.RoomsByRate?.[0]?.RoomRateById?.Price;
      return orderPerLowestPrice(optALowestPrice, optBLowestPrice);
    });
  } else {
    return validRoomsByRate;
  }
}

function roomsByRatePerLowestPrice(roomsByRate: HotelSearchedRoom[]): HotelSearchedRoom[] {
  return roomsByRate ? [...roomsByRate].sort((optA: HotelSearchedRoom, optB: HotelSearchedRoom) => {
    return orderPerLowestPrice(optA?.RoomRateById?.Price, optB?.RoomRateById?.Price);
  }) : [];
}

function roomRatesPerLowestPrice(roomRates: HotelSearchedRoomRate[]): HotelSearchedRoomRate[] {
  return roomRates ? [...roomRates].sort((optA: HotelSearchedRoomRate, optB: HotelSearchedRoomRate) => {
    return orderPerLowestPrice(optA?.Price, optB?.Price);
  }) : [];
}

function orderPerLowestPrice(optPriceA: Price, optPriceB: Price): number {
  return optPriceA?.Total - optPriceB?.Total;
}

export const exportedForTesting = {
  mapRoomsGroup,
  roomsByRateOnRoomsByCodePerLowestPrice,
  roomsByCodePerLowestPrice,
};
